import { clearAuthTokens } from './tokenStorage';

export const TENANT_DISABLED_EVENT = 'safework:tenant-disabled';
export const TENANT_DISABLED_CODE = 'TENANT_DISABLED';
const TENANT_DISABLED_STORAGE_KEY = 'safework_tenant_disabled';

/** True after this tab has seen a TENANT_DISABLED response. Survives refresh. */
export function isTenantDisabledNow(): boolean {
  return sessionStorage.getItem(TENANT_DISABLED_STORAGE_KEY) === '1';
}

/** A live 200 from getOidcConfig means the safework row is active again. */
export function clearTenantDisabled(): void {
  sessionStorage.removeItem(TENANT_DISABLED_STORAGE_KEY);
}

function errorText(body: unknown): string {
  if (!body || typeof body !== 'object') {
    return '';
  }
  const record = body as { code?: unknown; error?: unknown; message?: unknown };
  if (typeof record.message === 'string') {
    return record.message;
  }
  if (typeof record.error === 'string') {
    return record.error;
  }
  if (record.error && typeof record.error === 'object') {
    const nested = record.error as { message?: unknown };
    if (typeof nested.message === 'string') {
      return nested.message;
    }
  }
  return '';
}

/**
 * True when a 403 body is the tenant-disabled entitlement gate.
 * Reads a clone so the caller can still consume `response`.
 */
export async function responseIsTenantDisabled(response: Response): Promise<boolean> {
  if (response.status !== 403) {
    return false;
  }
  const text = await response.clone().text();
  let body: unknown = null;
  try {
    body = JSON.parse(text);
  } catch {
    body = null;
  }
  if (body && typeof body === 'object') {
    const code = (body as { code?: unknown }).code;
    if (code === TENANT_DISABLED_CODE) {
      return true;
    }
    if (errorText(body).includes(TENANT_DISABLED_CODE)) {
      return true;
    }
  }
  return text.includes(TENANT_DISABLED_CODE);
}

function removeTenantInfoCacheKeys(): void {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && key.startsWith('tenant_info_cache')) {
      keys.push(key);
    }
  }
  for (const key of keys) {
    localStorage.removeItem(key);
  }
}

/**
 * Drop auth and tenant caches, then tell the app to show the disabled screen.
 * `tenantService` is imported dynamically so this module does not cycle through it.
 */
export async function markTenantDisabled(): Promise<void> {
  sessionStorage.setItem(TENANT_DISABLED_STORAGE_KEY, '1');
  clearAuthTokens();
  localStorage.removeItem('oidc_config_cache');
  removeTenantInfoCacheKeys();
  const tenantModule = await import('../services/tenantService');
  tenantModule.default.clearCache();
  window.dispatchEvent(new CustomEvent(TENANT_DISABLED_EVENT));
}
